import { ROLES } from '@inpart-io/devtools-iap';
import { GET_USER_TENANT_ACCESS, IS_ROLE, SESSION_MODULE } from '@inpart-io/devtools-vue';
import { CreationPermissionSettingValues, type IDataObjectPermission } from '@inpart-io/padme-iap';
import { computed, type ComputedRef } from 'vue';
import { useStore } from 'vuex';

import { IS_ALLIANCE, USE_ADVANCED } from '@/store/session-module/getters';
import { TENANT_CONFIGURATION_MODULE } from '@/store/tenant-configuration-module';
import { GET_UNIQUE } from '@/store/tenant-configuration-module/getters';

export function usePermissionHelper(): {
    isAdvanced: ComputedRef<boolean>;
    isAlliance: ComputedRef<boolean>;
    isContributor: ComputedRef<boolean>;
    isBusinessAdmin: ComputedRef<boolean>;
    isManager: ComputedRef<boolean>;
    isTechnicalAdmin: ComputedRef<boolean>;
    isClientAdmin: ComputedRef<boolean>;
    isTenantAdmin: ComputedRef<boolean>;
    canCreateBrief: ComputedRef<boolean>;
    currentUserRoleLabels: ComputedRef<string[]>;
    canDelete: (target: IDataObjectPermission) => boolean;
    canEditObject: (target: IDataObjectPermission) => boolean;
    canEdit: (target: IDataObjectPermission, fieldName?: string) => boolean;
    canEditLinkedObject: (target: IDataObjectPermission, fieldName?: string) => boolean;
} {
    const store = useStore();

    const isContributor = computed<boolean>(() => {
        return store.getters[`${SESSION_MODULE}/${IS_ROLE}`](ROLES.CONTRIBUTOR);
    });

    const isBusinessAdmin = computed<boolean>(() => {
        return store.getters[`${SESSION_MODULE}/${IS_ROLE}`](ROLES.BUSINESS_ADMIN);
    });

    const isManager = computed<boolean>(() => {
        return store.getters[`${SESSION_MODULE}/${IS_ROLE}`](ROLES.MANAGER);
    });

    const isTechnicalAdmin = computed<boolean>(() => {
        return store.getters[`${SESSION_MODULE}/${IS_ROLE}`](ROLES.TECHNICAL_ADMIN);
    });

    const isClientAdmin = computed<boolean>(() => {
        return store.getters[`${SESSION_MODULE}/${IS_ROLE}`](ROLES.CLIENT_ADMIN);
    });

    const isTenantAdmin = computed<boolean>(() => {
        return store.getters[`${SESSION_MODULE}/${IS_ROLE}`](ROLES.TENANT_ADMIN);
    });

    const isAdvanced = computed<boolean>(() => {
        return store.getters[`${SESSION_MODULE}/${USE_ADVANCED}`];
    });

    const isAlliance = computed<boolean>(() => {
        return store.getters[`${SESSION_MODULE}/${IS_ALLIANCE}`];
    });

    const canDelete = (target = null as IDataObjectPermission): boolean => {
        return target?.deletable ?? false;
    };

    const canEditObject = (target = null as IDataObjectPermission): boolean => {
        return target?.editable ?? false;
    };

    const canEdit = (target = null as IDataObjectPermission, fieldName = null as string): boolean => {
        const isReadOnlyField = target?.readOnlyFields?.includes(fieldName) ?? false;
        return isReadOnlyField ? false : target?.editable ?? false;
    };

    const canEditLinkedObject = (target = null as IDataObjectPermission, fieldName = null as string): boolean => {
        return !(target?.readOnlyFields?.includes(fieldName) ?? true);
    };

    const canCreateBrief = computed<boolean>(() => {
        const tenantConfig = store.getters[`${TENANT_CONFIGURATION_MODULE}/${GET_UNIQUE}`];
        if (isBusinessAdmin.value || isTechnicalAdmin.value) {
            return true;
        } else if (isManager.value) {
            return tenantConfig.allowedRoleForBriefCreation !== CreationPermissionSettingValues.ADMINISTRATION_ONLY;
        }

        return false;
    });

    const currentUserRoleLabels = computed<string[]>(() => {
        return store.getters[`${SESSION_MODULE}/${GET_USER_TENANT_ACCESS}`]?.applicationRoles.map((roleId: number) => {
            return Object.keys(ROLES)[Object.values(ROLES).indexOf(roleId)];
        });
    });

    return {
        isAdvanced,
        isAlliance,
        isContributor,
        isBusinessAdmin,
        isManager,
        isTechnicalAdmin,
        isClientAdmin,
        isTenantAdmin,
        currentUserRoleLabels,
        canCreateBrief,
        canDelete,
        canEditObject,
        canEdit,
        canEditLinkedObject
    };
}
